/**
 * Email store — core slice (count helpers, layout mutators, persistence).
 */
import { effect } from '@preact/signals'
import {
  sidebarDeltasForTransition,
  applySidebarDeltas,
  applyListMetaDelta,
  adjustCountKey,
  threadFactsFromMobileThread,
} from '@tshmanager/email-shared'

export function attachCore(ctx) {
  // De-dupe sidebar count pulls (DnD / cable / mutations can race).
  ctx._sidebarCountsSeq = 0
  ctx._sidebarCountsInflight = null

  ctx._resolveSourceFolderId = function _resolveSourceFolderId(emailRow) {
    if (emailRow?.folder_id != null && emailRow.folder_id !== '') {
      return Number(emailRow.folder_id)
    }
    // Infer from active custom folder when row lacks folder_id.
    const folder = ctx.activeFolder.value
    if (typeof folder === 'string' && folder.startsWith('folder_')) {
      const n = Number(folder.slice(7))
      return Number.isFinite(n) ? n : null
    }
    return null
  }

  /** Thread facts for sidebar/list meta — canonical Thread shape. */
  ctx._threadFacts = function _threadFacts(emailRow) {
    if (!emailRow) {
      return { unread: false, archived: false, trashed: false, spam: false, starred: false, emailFolderId: null }
    }
    const facts = threadFactsFromMobileThread(emailRow, ctx.activeFolder.value)
    if (facts.emailFolderId == null) {
      const inferred = ctx._resolveSourceFolderId(emailRow)
      if (inferred != null) facts.emailFolderId = inferred
    }
    return facts
  }

  /**
   * Single entry for optimistic count updates (sidebar + optional list meta).
   * All mutations must go through here — never patch sidebarCounts / emailsMeta ad-hoc.
   * Pass skipListMeta when _removeEmailRow will apply leaveView meta.
   */
  ctx._applyCountTransition = function _applyCountTransition(before, after, { leaveView = false, unreadDelta = null, skipListMeta = false } = {}) {
    const deltas = sidebarDeltasForTransition(before, after)
    if (Object.keys(deltas).length > 0) {
      ctx.sidebarCounts.value = applySidebarDeltas(ctx.sidebarCounts.value || {}, deltas)
    }

    if (skipListMeta) return

    if (leaveView) {
      ctx.emailsMeta.value = applyListMetaDelta(ctx.emailsMeta.value, {
        leaveView: true,
        wasUnread: before.unread,
      })
    } else if (unreadDelta) {
      ctx.emailsMeta.value = applyListMetaDelta(ctx.emailsMeta.value, { unreadDelta })
    } else if (before.unread !== after.unread && !before.archived && !before.trashed) {
      // Mark read/unread while staying in the current list view.
      ctx.emailsMeta.value = applyListMetaDelta(ctx.emailsMeta.value, {
        unreadDelta: after.unread ? 1 : -1,
      })
    }
  }

  ctx._adjustCount = function _adjustCount(folderKey, delta) {
    if (!delta || !folderKey) return
    ctx.sidebarCounts.value = adjustCountKey(ctx.sidebarCounts.value || {}, folderKey, delta)
  }

  // When a row leaves the current list view, keep header total/unread in sync.
  ctx._removeEmailRow = function _removeEmailRow(emailId, { skipMeta = false } = {}) {
    const row = ctx.emails.value.find(e => e.id === Number(emailId) || e.id === emailId)
    if (!row) {
      ctx.emails.value = ctx.emails.value.filter(e => e.id !== Number(emailId) && e.id !== emailId)
      return
    }
    ctx.emails.value = ctx.emails.value.filter(e => e.id !== row.id)
    if (!skipMeta) {
      ctx.emailsMeta.value = applyListMetaDelta(ctx.emailsMeta.value, {
        leaveView: true,
        wasUnread: row.read === false,
      })
    }
  }

  // ── Persistence effects ──────────────────────────────────────────────────

  effect(() => {
    localStorage.setItem('email_sidebar_collapsed', String(ctx.sidebarCollapsed.value))
  })

  effect(() => {
    localStorage.setItem('email_list_width', String(ctx.listWidth.value))
  })

  // ── Layout mutators ──────────────────────────────────────────────────────

  ctx.usesStackedNav = function usesStackedNav() {
    return ctx.isMobileLayout.value || ctx.isCompactNavLayout.value
  }

  ctx.toggleSidebar = function toggleSidebar() {
    if (ctx.usesStackedNav()) {
      ctx.activePanel.value = ctx.activePanel.value === 'sidebar' ? 'list' : 'sidebar'
    } else {
      ctx.sidebarCollapsed.value = !ctx.sidebarCollapsed.value
    }
  }

  ctx.showSidebarPanel = function showSidebarPanel() {
    if (ctx.usesStackedNav()) ctx.activePanel.value = 'sidebar'
  }

  ctx.showListPanel = function showListPanel() {
    if (ctx.usesStackedNav()) ctx.activePanel.value = 'list'
  }

  ctx.showReadingPane = function showReadingPane() {
    if (ctx.isMobileLayout.value) ctx.activePanel.value = 'pane'
  }

  ctx.setListWidth = function setListWidth(px) {
    ctx.listWidth.value = Math.max(200, Math.min(600, px))
  }

  ctx.setLayoutMode = function setLayoutMode(width) {
    const mobile = width < 768
    const compact = width >= 768 && width < 1400
    ctx.batch(() => {
      ctx.isMobileLayout.value = mobile
      ctx.isCompactNavLayout.value = compact
      if (mobile) {
        if (ctx.activePanel.value === null) ctx.activePanel.value = 'list'
      } else if (compact) {
        if (ctx.activePanel.value === null || ctx.activePanel.value === 'pane') {
          ctx.activePanel.value = 'list'
        }
      } else {
        ctx.activePanel.value = null
      }
    })
  }

  // Backward-compatible alias for layout manager
  ctx.setMobileLayout = function setMobileLayout(mobile) {
    ctx.setLayoutMode(mobile ? 767 : 1400)
  }
}
